import type { StudentPerformanceResponse } from '@/types/Response/PerformanceResponse';
import {
    StudentRatingTimeInformation,
    type StudentRatingPerformance,
    type StudentRatingTiming,
    type StudyUnitStudyQuizPerformance,
} from '@/types/Performance';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import { fsumClient } from '@/services/FsumHttpClient';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type {
    ExamFoil,
    ExamQuestion,
    Foil,
    Question,
    QuestionFoil,
    StudyQuestion,
} from '@/types/TestReview';
import { formatQuestionImage } from '@/functions/formatQuestionImage';

async function getStudentRatingPerformance(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<StudentRatingPerformance> {
    const performanceResult: Promise<StudentPerformanceResponse> = fsumClient.get(
        `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/performance`,
    );

    return performanceResult.then((response: StudentPerformanceResponse) => {
        const studentRatingPerformance: StudentRatingPerformance = {
            ...response,
            student,
        };

        return studentRatingPerformance;
    });
}

async function getStudentStudyQuizzes(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<StudyUnitStudyQuizPerformance[]> {
    return fsumClient.get(
        `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/performance/study-questions`,
    );
}

async function getStudentRatingTiming(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<StudentRatingTimeInformation> {
    const timingResult: Promise<StudentRatingTiming> = fsumClient.get(
        `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/time-intervals`,
    );

    return timingResult.then(
        (response: StudentRatingTiming) => new StudentRatingTimeInformation(
            response.studyUnitTimes,
            response.stageTimes,
            response.eocTime,
            response.practiceTimes,
            response.totalTime,
            response.requiredTime,
        ),
    );
}

async function getStudentStudySession(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
    studyUnit: string,
): Promise<Question[]> {
    const studySession: Promise<StudyQuestion[]> = fsumClient.get(
        `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/study-units/${studyUnit}/study-questions`,
    );

    return studySession.then(
        (responses: StudyQuestion[]) => {
            const questions: Question[] = responses.map((response: StudyQuestion) => (
                {
                    number: response.orderNum,
                    stem: formatQuestionImage(response.stem, course.certificate.part),
                    foils: response.foils.map((foil: Foil) => (
                        {
                            ...foil,
                            explanation: formatQuestionImage(foil.explanation, course.certificate.part),
                            letter: foil.letter.toUpperCase(),
                            correct: foil.isCorrectAnswer,
                        }
                    )),
                    marked: response.isMarked,
                    isCorrect: response.isCorrect,
                    selected: (response.firstAnswer?.toUpperCase() || null),
                    visible: response.orderNum === 1,
                    subUnit: response.subUnit,
                }
            ));

            return questions;
        },
    );
}

async function getStudentTestSession(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
    testSessionId: number,
): Promise<Question[]> {
    const testSession: Promise<ExamQuestion[]> = fsumClient.get(
        `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/tests/${testSessionId}`,
    );

    return testSession.then(
        (responses: ExamQuestion[]) => {
            const questions: Question[] = responses.map((response: ExamQuestion) => (
                {
                    number: response.sequenceNumber,
                    stem: formatQuestionImage(response.studyQuestion.stem, course.certificate.part),
                    foils: response.examFoils.map((foil: ExamFoil) => {
                        const studyQuestionFoil: Foil|undefined = response.studyQuestion.foils.find(
                            (questionFoil: Foil) => (
                                questionFoil.letter.toUpperCase() === foil.foilLetter
                            ),
                        );

                        if (!studyQuestionFoil) {
                            throw new Error(`No matching studyQuestionFoil found for foilLetter: ${foil.foilLetter}`);
                        }

                        return {
                            ...studyQuestionFoil,
                            explanation: formatQuestionImage(studyQuestionFoil.explanation, course.certificate.part),
                            letter: foil.letter.toUpperCase(),
                            correct: studyQuestionFoil.isCorrectAnswer,
                        };
                    }),
                    marked: response.isMarked,
                    isCorrect: response.isCorrect,
                    selected: (response.firstAnswer?.toUpperCase() || null),
                    visible: response.sequenceNumber === 1,
                    subUnit: response.studyQuestion.subUnit,
                }
            ));

            // sort the foils by letter, ExamFoils are not in the same order as StudyQuestion.foils
            questions.forEach((question: Question) => {
                question.foils.sort((a: QuestionFoil, b: QuestionFoil) => {
                    if (a.letter > b.letter) {
                        return 1;
                    }

                    if (a.letter < b.letter) {
                        return -1;
                    }

                    return 0;
                });
            });

            return questions;
        },
    );
}

export default {
    getStudentRatingPerformance,
    getStudentStudyQuizzes,
    getStudentRatingTiming,
    getStudentStudySession,
    getStudentTestSession,
};
